/**
 * Die Zahlungsvereinbarung eines Angebots — WARTESCHLANGE Punkt 45.
 *
 * ## Der Anlass
 *
 * Antoine, 17.09.2026: „Die Zahlungskonditionen sind noch ein großes Durcheinander. […] Das meinte
 * ich, als ich schrieb, dass die Zahlungskonditionen noch Chaos sind! Genau das was jetzt bei
 * Kokorov passiert ist."
 *
 * Der Beleg: **„Anzahlung 100 %" zusammen mit „Anzahlungsart: Keine" war speicherbar.** Der Gast
 * nahm an, es kam keine Zahlungsseite, und die Veranstaltung war am nächsten Abend. Die Ursache
 * liegt nicht im Ablauf, sondern im Modell: `depositMethod: "none"` heißt gleichzeitig „keine
 * Anzahlung" UND wirkt wie „keine Zahlung". Elf Felder, die einander widersprechen können.
 *
 * ## Das Modell in einem Satz
 *
 * Ein Angebot hat höchstens ZWEI Zahlungen — eine optionale Anzahlung und den Rest — und
 *
 *     > jeder fällige Betrag hat genau einen Kanal, und der Kanal muss funktionieren.
 *
 * Aus elf Feldern werden sieben Angaben, von denen keine Kombination unbezahlbar ist.
 *
 * ## Warum diese Datei im geteilten Paket liegt
 *
 * Weil dieselbe Umdeutung sonst dreimal entsteht: in der API beim Annehmen, in der Oberfläche beim
 * Bearbeiten, und im Angebotsdokument beim Drucken. Genau das ist in dieser Woche schon einmal
 * passiert — `alsSektionsModus` lag als Kopie in drei Schichten, und eine davon verschluckte
 * `total_event`; die Rechnungsposition wäre „6.500,00 € × 12 Personen" = 78.000 € gewesen.
 * Dieselbe Bauart steckte hinter RE0086. Eine Wahrheit, ein Ort.
 *
 * ## Was diese Datei NICHT tut
 *
 * Sie schreibt nichts und liest keine Datenbank. Sie ist eine Tabelle: aus dem, was gespeichert
 * ist, wird das, was gilt — und sie sagt, was daran nicht zusammenpasst. Alles Weitere entscheiden
 * die Aufrufer.
 */

/** Die vier Wege, auf denen Geld tatsächlich fließen kann. */
export type Kanal = "stripe" | "ueberweisung" | "vor_ort" | "kostenuebernahme";

/**
 * Bei der Restzahlung gibt es KEINE Kostenübernahme.
 *
 * Antoine, 18.09.2026: „Anstatt Anzahlung sollte auch Kostenübernahme möglich sein. Bei Restzahlung
 * macht es keinen Sinn." Fachlich schlüssig: die Kostenübernahme ist die Zusage eines Dritten, die
 * an die Stelle einer VORauszahlung tritt. Bei der Restzahlung gibt es nichts abzusichern.
 */
export type RestKanal = Exclude<Kanal, "kostenuebernahme">;

/** Wann die Anzahlung fällig ist — gemessen ab der ANNAHME. */
export type AnzahlungsFrist = { art: "sofort" } | { art: "tage_nach_annahme"; tage: number };

/**
 * Wann die Restzahlung fällig ist — gemessen am VERANSTALTUNGSTAG.
 *
 * KORREKTUR 18.09.2026, morgens. Am Vorabend hatte Antoine „danach" ausgeschlossen („Nein, das löst
 * dann ggf. eine Nachrechnung aus"). Am Morgen darauf, vor dem konkreten Vorgang Clara Emmerich —
 * 20.170,50 €, vier Kostenübernahmen, Abrechnung nach dem Event — hat er es zurückgenommen: „Doch,
 * nach der Veranstaltung ist okay."
 *
 * Die spätere Aussage gilt. Sie ist auch die belastbarere: im Catering IST die Abrechnung nach dem
 * Termin üblich, und ein laufender Auftrag über 20.000 € hing daran.
 */
export type RestzahlungsFrist =
  | { art: "tage_vor_event"; tage: number }
  | { art: "am_event_tag" }
  | { art: "tage_nach_event"; tage: number };

export interface Anzahlung {
  /** Prozent der Angebotssumme in Basispunkten — ODER `betragCents`, nie beides (s. `pruefe…`). */
  prozentBp: number | null;
  betragCents: number | null;
  kanal: Kanal;
  frist: AnzahlungsFrist;
}

export interface Restzahlung {
  kanal: RestKanal;
  frist: RestzahlungsFrist;
}

export interface Zahlungsvereinbarung {
  /** `null` = ausdrücklich keine Anzahlung. Dann ist die Restzahlung die GESAMTzahlung. */
  anzahlung: Anzahlung | null;
  /**
   * `null` = es bleibt nichts übrig, weil die Anzahlung 100 % beträgt.
   *
   * Nicht „ausgegraut", nicht „ignoriert" — weg. Ein Feld, das nichts bewirkt, ist eine Falle.
   */
  restzahlung: Restzahlung | null;
  /** Bis wann das Angebot gilt (ISO-Datum) — unabhängig von beiden Zahlungen. */
  gueltigBis: string | null;
  /**
   * Bleibt hier und je Angebot überschreibbar. Antoine, 18.09.2026: „Storno soll bei
   * Zahlungskonditionen bleiben, damit das immer individuell angepasst werden kann."
   */
  stornobedingungen: string | null;
}

/**
 * Was der Betrieb tatsächlich kann — entscheidet, welche Kanäle überhaupt wählbar sind.
 *
 * Kein Kanal ist wählbar, nur weil es ihn gibt. „Überweisung" ohne hinterlegte Bankverbindung ist
 * eine Zahlungsaufforderung ohne Kontonummer; „Kostenübernahme" ohne eSignatur-Zugang ist ein
 * Vertrag, den niemand unterschreiben kann.
 */
export interface Bereitschaft {
  /** Der Betrieb kann über Stripe kassieren (eigenes Konto ODER Inhaber des Systemzugangs). */
  stripe: boolean;
  /** Eine Bankverbindung ist in den Betriebsstammdaten hinterlegt. */
  bankverbindung: boolean;
  /** Ein eSignatur-Zugang ist hinterlegt (Kostenübernahme-Verträge). */
  kostenuebernahme: boolean;
}

/** „Vor Ort" kann jeder Betrieb, immer — dafür braucht es nichts als eine Kasse. */
export function kanalVerfuegbar(kanal: Kanal, b: Bereitschaft): boolean {
  switch (kanal) {
    case "vor_ort":
      return true;
    case "stripe":
      return b.stripe;
    case "ueberweisung":
      return b.bankverbindung;
    case "kostenuebernahme":
      return b.kostenuebernahme;
  }
}

/**
 * Warum ein Kanal nicht wählbar ist — als Satz, den der Betreiber lesen und befolgen kann.
 *
 * Antoine, 13.09.2026: er will bei jedem Fehler die konkrete Ursache sehen, nicht „nicht möglich".
 * Deshalb nennt jeder Satz, WAS fehlt und WO es steht.
 */
export function grundWarumNichtVerfuegbar(kanal: Kanal, b: Bereitschaft): string | null {
  if (kanalVerfuegbar(kanal, b)) return null;
  switch (kanal) {
    case "stripe":
      return "Für diesen Betrieb ist kein Stripe-Konto verbunden. Einstellungen → Module → Stripe-Zahlungen.";
    case "ueberweisung":
      return "Für diesen Betrieb ist keine Bankverbindung hinterlegt. Einstellungen → Betrieb.";
    case "kostenuebernahme":
      return "Für diesen Betrieb ist kein eSignatur-Zugang hinterlegt. Einstellungen → Module → E-Signatur.";
    default:
      return null;
  }
}

/**
 * „Sofort" kann nur Stripe.
 *
 * Red Team (3) im Konzept: eine Überweisung lässt sich nicht „sofort" tätigen. Das System erwartete
 * sonst eine Zahlung, die in dieser Frist nie kommt — und mahnt. Bei allen anderen Kanälen beginnt
 * die Auswahl bei einem Tag.
 */
export function sofortMoeglich(kanal: Kanal): boolean {
  return kanal === "stripe";
}

/** Ein Befund an der Vereinbarung: ein Satz, und wo er hingehört. */
export interface Befund {
  feld: "anzahlung" | "anzahlung.kanal" | "anzahlung.betrag" | "anzahlung.frist" | "restzahlung.kanal" | "restzahlung.frist";
  satz: string;
  /**
   * Gesetzt, wenn der Befund an der EINRICHTUNG des Betriebs hängt (Stripe nicht verbunden, keine
   * Bankverbindung, kein eSignatur-Zugang) — nicht an der Vereinbarung selbst.
   *
   * Der Unterschied entscheidet, wie hart darauf reagiert wird. Ein STRUKTURELLER Befund („Betrag
   * ohne Weg") ist immer falsch und wird immer abgelehnt. Ein Einrichtungs-Befund dagegen kann aus
   * dem geerbten Betriebs-Standard stammen — und dann wäre es falsch, deswegen eine ganz andere
   * Änderung zu blockieren.
   *
   * Beim Bauen am 18.09.2026 genau so passiert: ein frisch angelegter Betrieb hat als Standard
   * „Stripe sofort", aber noch kein Stripe verbunden. Ohne diese Unterscheidung hätte er KEIN
   * Angebot mehr bearbeiten können — auch nicht die Restzahlung, die mit Stripe nichts zu tun hat.
   * Eine Sicherung, die die normale Arbeit blockiert, wird umgangen und schützt dann gar nichts.
   */
  kanal?: Kanal;
  /**
   * Ob der Befund das SPEICHERN verhindert oder nur danebensteht.
   *
   * Hart blockiert wird nur, was technisch gar nicht ausführbar ist: Stripe ohne verbundenes Konto
   * (es entsteht keine Zahlungsseite) und Kostenübernahme ohne eSignatur-Zugang (es entsteht kein
   * Vertrag) — beides Sackgassen, in die der Gast sonst hineinläuft.
   *
   * Eine fehlende Bankverbindung ist KEINE Sackgasse: die Rechnung entsteht trotzdem, und die
   * Kontodaten stehen bei den meisten Betrieben ohnehin im Rechnungsprogramm. Das Speichern
   * deswegen zu verweigern hätte gewöhnliche Arbeit blockiert — und eine Sicherung, die die
   * Arbeit blockiert, wird umgangen und schützt dann gar nichts. Der Betrieb sieht den fehlenden
   * Weg stattdessen in der Maske ausgegraut.
   */
  schwere: "blockierend" | "hinweis";
}

/** 100 % in Basispunkten — die Grenze, ab der es nichts mehr zu regeln gibt. */
const VOLL_BP = 10_000;

/**
 * Ist die Anzahlung der ganze Betrag? Dann entfällt die Restzahlung.
 *
 * ## Die Kostenübernahme zählt hier NIE mit
 *
 * Antoine, 18.09.2026: „Kostenübernahme ist ein Vertrag und ersetzt die Anzahlung. Die Anzahlung
 * ist eine Sicherheitsleistung, wie ein Pfand, der von der Kostenübernahme ersetzt wird."
 *
 * Das ist der Satz, der das Modell klärt: eine Kostenübernahme ist **kein Geld**, sondern die
 * Zusage eines Dritten, die an die Stelle des Pfands tritt. Es ist also nichts vorausbezahlt — die
 * volle Summe bleibt als Restzahlung offen, auch wenn der Vertrag den ganzen Auftrag deckt.
 *
 * Eine frühere Fassung dieser Funktion hat bei „Kostenübernahme über 100 %" die Restzahlung
 * verschwinden lassen. Beim Vorgang Clara Emmerich (20.170,50 €) wären damit 20.170,50 € aus der
 * Forderung gefallen, obwohl kein Cent geflossen ist.
 */
export function istVollanzahlung(a: Anzahlung | null, gesamtCents: number | null): boolean {
  if (!a) return false;
  if (a.kanal === "kostenuebernahme") return false;
  // Reihenfolge wie in `computeDepositCents`: der feste Betrag hat Vorrang vor dem Prozentsatz.
  // Stünde sie hier andersherum, entschiede über „ist alles gedeckt?" eine andere Zahl als über
  // „wie viel wird abgebucht?" — und genau solche zwei Wahrheiten sind der Kern von Punkt 45.
  if (a.betragCents != null && a.betragCents > 0 && gesamtCents != null && gesamtCents > 0) return a.betragCents >= gesamtCents;
  if (a.prozentBp != null) return a.prozentBp >= VOLL_BP;
  return false;
}

/**
 * Prüft, ob die Vereinbarung speicherbar ist. Leere Liste = in Ordnung.
 *
 * DIESE FUNKTION IST DER KERN VON PUNKT 45. Sie wird beim SPEICHERN angewandt, nicht als Warnung
 * angezeigt: der kaputte Zustand darf gar nicht erst entstehen. Der Fall vom 17.09.2026 —
 * „100 % Anzahlung" ohne funktionierenden Kanal — ist hier nicht mehr darstellbar.
 */
/** Siehe `Befund.schwere`: nur die technisch unausführbaren Wege blockieren das Speichern. */
function schwereFuerKanal(kanal: Kanal): "blockierend" | "hinweis" {
  return kanal === "stripe" || kanal === "kostenuebernahme" ? "blockierend" : "hinweis";
}

export function pruefeVereinbarung(v: Zahlungsvereinbarung, b: Bereitschaft, gesamtCents: number | null = null): Befund[] {
  const befunde: Befund[] = [];

  if (v.anzahlung) {
    const a = v.anzahlung;

    /*
     * Eine Anzahlung braucht eine Höhe. Dass BEIDE Angaben dastehen, ist dagegen KEIN Fehler:
     * seit jeher gilt „fester Betrag hat Vorrang vor Prozent" (`computeDepositCents`,
     * apps/api/src/lib/payment-terms.ts, mit eigenem Test).
     *
     * Eine frühere Fassung hat das hier als Widerspruch abgelehnt — und damit einen gewöhnlichen
     * Vorgang blockiert: ein Angebots-Override kann den Prozentsatz des Betriebs-Standards gar
     * nicht löschen (`resolvePaymentTerms` überliest `null` als „nicht überschrieben"), also
     * stehen nach dem Zusammenführen regelmäßig beide da. Die Regel hätte das normale Setzen eines
     * Fixbetrags unmöglich gemacht.
     */
    const hatProzent = a.prozentBp != null && a.prozentBp > 0;
    const hatBetrag = a.betragCents != null && a.betragCents > 0;
    if (!hatProzent && !hatBetrag) {
      befunde.push({ feld: "anzahlung.betrag", schwere: "blockierend", satz: "Bitte angeben, wie hoch die Anzahlung ist – als Prozentsatz oder als fester Betrag." });
    }
    if (a.prozentBp != null && a.prozentBp > VOLL_BP) {
      befunde.push({ feld: "anzahlung.betrag", schwere: "blockierend", satz: "Eine Anzahlung über 100 % ist nicht möglich." });
    }

    const grund = grundWarumNichtVerfuegbar(a.kanal, b);
    if (grund) befunde.push({ feld: "anzahlung.kanal", satz: grund, kanal: a.kanal, schwere: schwereFuerKanal(a.kanal) });

    if (a.frist.art === "sofort" && !sofortMoeglich(a.kanal)) {
      befunde.push({
        feld: "anzahlung.frist",
        schwere: "blockierend",
        satz: "„Sofort“ ist nur bei Stripe möglich – eine Überweisung oder eine Zahlung vor Ort braucht mindestens einen Tag.",
      });
    }
    if (a.frist.art === "tage_nach_annahme" && a.frist.tage < 1) {
      befunde.push({ feld: "anzahlung.frist", schwere: "blockierend", satz: "Die Frist muss mindestens einen Tag betragen." });
    }
  }

  // Die Restzahlung ist Pflicht, SOLANGE etwas übrig bleibt. Das ist die Stelle, an der der
  // gestrige Ausfall entstand: „keine Anzahlung" nahm der Gesamtzahlung ihren Kanal.
  const voll = istVollanzahlung(v.anzahlung, gesamtCents);
  if (voll && v.restzahlung) {
    befunde.push({ feld: "restzahlung.kanal", schwere: "blockierend", satz: "Bei einer Anzahlung über den vollen Betrag bleibt keine Restzahlung übrig." });
  }
  if (!voll && !v.restzahlung) {
    befunde.push({
      feld: "restzahlung.kanal",
      schwere: "blockierend",
      satz: v.anzahlung
        ? "Bitte angeben, wie die Restzahlung erfolgt – sonst kann der Gast den Rest nicht bezahlen."
        : "Bitte angeben, wie die Zahlung erfolgt – sonst kann der Gast gar nicht bezahlen.",
    });
  }
  if (!voll && v.restzahlung) {
    const grund = grundWarumNichtVerfuegbar(v.restzahlung.kanal, b);
    if (grund) befunde.push({ feld: "restzahlung.kanal", satz: grund, kanal: v.restzahlung.kanal, schwere: schwereFuerKanal(v.restzahlung.kanal) });
    // „Nach der Veranstaltung" ist seit dem 18.09.2026 ausdrücklich erlaubt (s. RestzahlungsFrist).
    // Geprüft wird nur noch, dass eine Tagesangabe keine negative Zahl ist — sonst stünde dieselbe
    // Frist zweimal im Modell, einmal als Vorzeichen und einmal als Art.
    const f = v.restzahlung.frist;
    if ((f.art === "tage_vor_event" || f.art === "tage_nach_event") && f.tage < 0) {
      befunde.push({ feld: "restzahlung.frist", schwere: "blockierend", satz: "Bitte eine Anzahl Tage ohne Minuszeichen angeben." });
    }
  }

  return befunde;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Bestandsdaten — die Übersetzung beim LESEN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Die elf Felder, wie sie heute gespeichert sind. Bewusst lose getippt: es ist Altbestand. */
export interface AlteKonditionen {
  depositPercentBp?: number | null;
  depositAmountCents?: number | null;
  depositMethod?: string | null;
  depositDueDays?: number | null;
  finalPaymentMethod?: string | null;
  finalPaymentMode?: string | null;
  finalPaymentDueDaysBeforeEvent?: number | null;
  paymentPathway?: string | null;
  /** Punkt 116 (Migration 518): Tage NACH der Veranstaltung, nur bei `invoice_after_event`. */
  finalPaymentDueDaysAfterEvent?: number | null;
  /** Punkt 116: ausschließlich das Zahlungsziel „N Tage nach Rechnungserhalt" (LexOffice). */
  paymentTermDays?: number | null;
  paymentTermText?: string | null;
  cancellationTerms?: string | null;
  offerValidUntil?: string | null;
}

export interface Uebersetzung {
  vereinbarung: Zahlungsvereinbarung;
  /**
   * Was beim Übersetzen NICHT eins zu eins aufging — als Sätze für den Betreiber.
   *
   * Sie sind der Grund, warum die Umstellung beim LESEN passiert und nicht in einer Migration:
   * ein stiller Datenumbau hätte denselben Fall (Kokorov) stillschweigend geradegezogen. So sieht
   * der Betrieb beim Öffnen, was sich geändert hat, und entscheidet selbst.
   */
  hinweise: string[];
}

function kanalAusAlt(wert: string | null | undefined): Kanal | null {
  switch (wert) {
    case "stripe_immediate":
    case "stripe_advance":
      return "stripe";
    case "onsite":
      return "vor_ort";
    case "invoice_advance":
    case "invoice_before_event":
    case "invoice_after_event":
      return "ueberweisung";
    default:
      return null;
  }
}

/**
 * Übersetzt gespeicherte Konditionen in das neue Modell. Ändert NICHTS an den Daten.
 *
 * Erwartet die ROHDATEN (Standard + Abweichung zusammengelegt, `konditionenRoh` in der API) —
 * nicht `resolvePaymentTerms`: das setzt seit Punkt 117 bei „Keine" den Betrag auf null, und der
 * Fall unten wäre dann unsichtbar.
 *
 * ## Der Fall, um den es geht
 *
 * Am Vorgang Kokorov stand „Anzahlung 100 %" zusammen mit „Anzahlungsart: Keine" und „Restzahlung:
 * Stripe vorab". Das alte Modell las daraus „keine Zahlung". Hier wird daraus, was offensichtlich
 * gemeint war — eine Anzahlung über 100 % per Stripe — und der Betrieb bekommt einen Satz dazu.
 * **Sichtbar korrigiert, nicht still.**
 */
export function alsZahlungsvereinbarung(alt: AlteKonditionen | null | undefined, gesamtCents: number | null = null): Uebersetzung {
  const hinweise: string[] = [];
  const a = alt ?? {};

  const prozentBp = a.depositPercentBp != null && a.depositPercentBp > 0 ? a.depositPercentBp : null;
  const betragCents = a.depositAmountCents != null && a.depositAmountCents > 0 ? a.depositAmountCents : null;
  const hatAnzahlungsbetrag = prozentBp != null || betragCents != null;

  // Die Restzahlung zuerst: ihr Kanal ist im Kokorov-Fall die Rettung für die Anzahlung.
  let restKanal: RestKanal = "vor_ort";
  const restAusAlt = kanalAusAlt(a.finalPaymentMethod) ?? (a.finalPaymentMode === "advance" ? "ueberweisung" : a.finalPaymentMode === "onsite" ? "vor_ort" : null);
  if (restAusAlt && restAusAlt !== "kostenuebernahme") restKanal = restAusAlt;

  let restFrist: RestzahlungsFrist =
    a.finalPaymentDueDaysBeforeEvent != null && a.finalPaymentDueDaysBeforeEvent > 0
      ? { art: "tage_vor_event", tage: a.finalPaymentDueDaysBeforeEvent }
      : { art: "am_event_tag" };

  if (a.finalPaymentMethod === "invoice_after_event") {
    // Antoine, 18.09.2026 morgens: „Doch, nach der Veranstaltung ist okay." Seit Punkt 116
    // (Migration 518) steht die Anzahl Tage in einem EIGENEN Feld; `paymentTermDays` ist nur noch
    // das Zahlungsziel nach Rechnungserhalt und wird hier nicht mehr gelesen.
    if (a.finalPaymentDueDaysAfterEvent != null) {
      restFrist = { art: "tage_nach_event", tage: a.finalPaymentDueDaysAfterEvent };
    } else {
      // KEINE Frist hinterlegt (genau der Fall Clara Emmerich). Vierzehn Tage sind das übliche
      // Zahlungsziel — aber geraten bleibt geraten, deshalb steht es sichtbar dabei.
      restFrist = { art: "tage_nach_event", tage: 14 };
      hinweise.push(
        "Die Restzahlung ist nach der Veranstaltung fällig, es war aber keine Frist hinterlegt. Eingetragen sind 14 Tage. Bitte prüfen.",
      );
    }
  }

  let anzahlung: Anzahlung | null = null;
  if (hatAnzahlungsbetrag) {
    let kanal = kanalAusAlt(a.depositMethod);
    if (a.depositMethod === "none") {
      // GENAU DER GESTRIGE FALL. Ein Betrag neben der ausdrücklichen Angabe „Anzahlungsart: Keine"
      // ist kein „keine Anzahlung", sondern eine Anzahlung, deren Weg fehlt — und der naheliegendste
      // Weg ist der, den die Restzahlung ohnehin nimmt.
      kanal = restKanal;
      hinweise.push(
        `Es war eine Anzahlung hinterlegt, aber kein Weg dafür („Anzahlungsart: Keine“). Genau daran ist am 17.09.2026 eine Zahlung ausgefallen. Die Anzahlung nimmt jetzt denselben Weg wie die Restzahlung. Bitte prüfen.`,
      );
    } else if (kanal === null) {
      /*
       * GAR KEINE ANGABE ist etwas ANDERES als die ausdrückliche Angabe „Keine" — und wird seit
       * jeher als Stripe gelesen: `faelligeZahlungNachAnnahme` (apps/api/src/lib/payment-terms.ts)
       * hat dafür den Zweig „Anzahlung per Stripe (oder ungesetzt = Bestandsverhalten)".
       *
       * Gefunden am 18.09.2026 an den echten Daten: der Hauptbetrieb hat 20 % Anzahlung, aber
       * keinen Anzahlungsweg hinterlegt — seine Anzahlungen laufen trotzdem über Stripe. Diese
       * Übersetzung hätte daraus „vor Ort" gemacht und damit über jeden laufenden Vorgang etwas
       * anderes behauptet, als tatsächlich passiert.
       */
      kanal = "stripe";
    }
    if (a.paymentPathway === "kostenuebernahme") {
      kanal = "kostenuebernahme";
    }
    /*
     * Punkt 116: die FAELLIGKEIT einer Zahlungszeile ist seitdem „leer = sofort"
     * (`anzahlungFaelligAm`). Hier — nur fuer die PRUEFUNG — bleibt es bei Ueberweisung/vor Ort
     * ohne Frist bei der bisherigen Lesart (7 Tage): sie als „sofort" zu lesen, lehnte den
     * Versand jedes solchen Angebots ab (public-offer/versand-Tests, Kokorov-Fall) — eine
     * Verschaerfung, die Antoine nicht entschieden hat. Offener Punkt im Bericht zu Punkt 116.
     */
    const frist: AnzahlungsFrist =
      a.depositDueDays != null && a.depositDueDays > 0
        ? { art: "tage_nach_annahme", tage: a.depositDueDays }
        : sofortMoeglich(kanal)
          ? { art: "sofort" }
          : { art: "tage_nach_annahme", tage: 7 };
    anzahlung = { prozentBp, betragCents, kanal, frist };
  } else if (a.paymentPathway === "kostenuebernahme") {
    // Kostenübernahme ohne eigenen Betrag: der Vertrag sichert den GANZEN Auftrag ab.
    //
    // Antoine, 18.09.2026: „Kostenübernahme ist ein Vertrag und ersetzt die Anzahlung. Die
    // Anzahlung ist eine Sicherheitsleistung, wie ein Pfand." Es ist also nichts vorausbezahlt —
    // die Restzahlung bleibt in voller Höhe bestehen (dafür sorgt `istVollanzahlung`).
    anzahlung = { prozentBp: VOLL_BP, betragCents: null, kanal: "kostenuebernahme", frist: { art: "tage_nach_annahme", tage: 7 } };
    hinweise.push(
      "Dieser Auftrag läuft über eine Kostenübernahme. Sie tritt an die Stelle der Anzahlung und sichert den vollen Betrag ab; bezahlt wird trotzdem die ganze Summe über die Restzahlung.",
    );
  }

  // Punkt 116: das Zahlungsziel („N Tage nach Rechnungserhalt" samt Zusatztext) ist wieder ein
  // eigenes, gültiges Feld (LexOffice-Zahlungsziel). Der frühere Hinweis „wird nicht mehr
  // verwendet" war damit falsch und ist entfallen.

  const voll = istVollanzahlung(anzahlung, gesamtCents);
  return {
    vereinbarung: {
      anzahlung,
      restzahlung: voll ? null : { kanal: restKanal, frist: restFrist },
      gueltigBis: a.offerValidUntil ?? null,
      stornobedingungen: a.cancellationTerms ?? null,
    },
    hinweise,
  };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Fälligkeiten — Punkt 116 (Antoine, 24.09.2026)
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Tage vor der Veranstaltung, wenn KEINE Frist hinterlegt ist. Bis Punkt 116 galt diese Zahl
 * IMMER (`FINAL_DUE_OFFSET_DAYS` in routes/payments.ts), gleich was in der Maske stand; jetzt nur
 * noch, wenn das Feld leer ist — damit Bestandsbetriebe ohne Angabe sich nicht verändern.
 */
export const RESTZAHLUNG_STANDARD_TAGE_VOR = 14;
/** Tage nach der Veranstaltung, wenn „Rechnung nach Event" ohne Tage hinterlegt ist (s. oben, Fall Clara Emmerich). */
export const RESTZAHLUNG_STANDARD_TAGE_NACH = 14;

/** Die Felder, aus denen sich die Fälligkeiten ergeben (Teilmenge der Zahlungsbedingungen). */
export interface Fristangaben {
  depositDueDays?: number | null;
  /**
   * Migration 718 (Antoine 13.10.2026): worauf sich `depositDueDays` bezieht.
   * 'nach_bestaetigung' (Vorgabe, auch bei leer) = ab Annahme/Versand gezählt,
   * 'vor_veranstaltung' = so viele Tage vor dem Veranstaltungstag.
   */
  depositDueMode?: string | null;
  finalPaymentMethod?: string | null;
  finalPaymentDueDaysBeforeEvent?: number | null;
  finalPaymentDueDaysAfterEvent?: number | null;
}

function plusTage(isoDatum: string, tage: number): string {
  const d = new Date(`${isoDatum.slice(0, 10)}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + tage);
  return d.toISOString().slice(0, 10);
}

/**
 * Wann die Anzahlung fällig ist: Datum der Annahme bzw. des Versands plus „Anzahlungs-Frist".
 * Leer (oder 0) heißt sofort — am selben Tag. Dieselbe Regel steht in SQL in
 * `reserve_public_deposit_payment` (sql/720); beide sind durch Tests aneinander gebunden.
 *
 * Modus 'vor_veranstaltung' (sql/720): Veranstaltung − Tage, aber nie vor dem Basisdatum
 * (dann heute fällig). Ohne Veranstaltungsdatum fällt er auf die Zählung ab Basis zurück.
 */
export function anzahlungFaelligAm(basisIso: string, k: Fristangaben | null | undefined, eventIso?: string | null): string {
  const tage = Math.max(0, k?.depositDueDays ?? 0);
  if (k?.depositDueMode === "vor_veranstaltung" && eventIso) {
    const vorEvent = plusTage(eventIso, -tage);
    const basis = basisIso.slice(0, 10);
    return vorEvent < basis ? basis : vorEvent;
  }
  return plusTage(basisIso, tage);
}

/**
 * Wann die Restzahlung fällig ist, gemessen am Veranstaltungstag:
 *   „Rechnung nach Event"  → Veranstaltung + Tage danach (leer: 14)
 *   sonst                  → Veranstaltung − Tage davor (0 = am Veranstaltungstag, leer: 14)
 * Erinnerung (13 Tage vor Fälligkeit, `list_due_payment_reminders`) und Überfällig
 * (`due_date < heute`) hängen an diesem Datum und ziehen damit automatisch mit.
 */
export function restzahlungFaelligAm(eventIso: string, k: Fristangaben | null | undefined): string {
  if (k?.finalPaymentMethod === "invoice_after_event") {
    return plusTage(eventIso, Math.max(0, k.finalPaymentDueDaysAfterEvent ?? RESTZAHLUNG_STANDARD_TAGE_NACH));
  }
  return plusTage(eventIso, -Math.max(0, k?.finalPaymentDueDaysBeforeEvent ?? RESTZAHLUNG_STANDARD_TAGE_VOR));
}
